// components/QuickStart/QuickStartTalk.js
// The quick-start's live test: talk to the new agent in the browser.
import React, { useEffect, useRef } from 'react';
import useAgentTestSession from '../../hooks/useAgentTestSession';

const formatSeconds = (total = 0) => `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;

const QuickStartTalk = ({ agent, companyName, stepper, orb, onFinished, onChoosePlan }) => {
    const {
        messages,
        callSummary,
        inCall,
        connecting,
        secondsElapsed,
        micError,
        errorCode,
        timeLimitReached,
        maxDurationSeconds,
        testsRemaining,
        startTest,
        endTest,
        startNewTest,
    } = useAgentTestSession(agent.agentId);
    const transcriptEnd = useRef(null);
    const name = agent.voiceName;

    useEffect(() => {
        transcriptEnd.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [messages]);

    // A test they ended themselves goes straight to the next steps.
    useEffect(() => {
        if (callSummary && !timeLimitReached) onFinished();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callSummary, timeLimitReached]);

    const testsLeftLine = testsRemaining === null
        ? ""
        : testsRemaining > 0
            ? `You have ${testsRemaining} test${testsRemaining === 1 ? "" : "s"} left on your trial.`
            : "That was your last test call on the trial.";

    if (errorCode === "TEST_LIMIT_REACHED") {
        return (
            <div className="qs-notice">
                <h3 id="qs-title" className="visually-hidden">Test calls used up</h3>
                <div className="qs-icon-tile"><i className="ri-time-line"></i></div>
                <h3 className="qs-title">You’ve used your test calls</h3>
                <p className="qs-text">{micError}</p>
                <div className="qs-actions w-100">
                    <button type="button" className="qs-btn qs-btn-ghost" onClick={onFinished}>See next steps</button>
                    <button type="button" className="qs-btn qs-btn-primary" onClick={onChoosePlan}>Choose a plan</button>
                </div>
            </div>
        );
    }

    if (errorCode === "MIC_DENIED") {
        return (
            <div className="qs-notice">
                <h3 id="qs-title" className="visually-hidden">Microphone blocked</h3>
                <div className="qs-icon-tile"><i className="ri-lock-line"></i></div>
                <h3 className="qs-title">Allow your microphone</h3>
                <p className="qs-text">Your browser blocked it. Click the lock next to the web address, allow the microphone, then try again.</p>
                <div className="qs-actions w-100">
                    <button type="button" className="qs-btn qs-btn-ghost" onClick={onFinished}>Skip the test</button>
                    <button type="button" className="qs-btn qs-btn-primary" onClick={() => startTest({})}>Try again</button>
                </div>
            </div>
        );
    }

    if (callSummary && timeLimitReached) {
        return (
            <div className="qs-notice">
                <h3 id="qs-title" className="visually-hidden">Test finished</h3>
                <div className="qs-icon-tile"><i className="ri-time-line"></i></div>
                <h3 className="qs-title">That’s the end of this test</h3>
                <p className="qs-text">
                    Tests last up to {Math.round((maxDurationSeconds || 180) / 60)} minutes. {testsLeftLine}
                </p>
                <div className="qs-actions w-100">
                    {testsRemaining !== 0 && (
                        <button type="button" className="qs-btn qs-btn-ghost" onClick={() => { startNewTest(); startTest({}); }}>Test again</button>
                    )}
                    <button type="button" className="qs-btn qs-btn-primary" onClick={onFinished}>See next steps</button>
                </div>
            </div>
        );
    }

    const live = inCall || connecting;

    return (
        <>
            {stepper}
            <h3 id="qs-title" className="visually-hidden">Talk to {name}</h3>
            <div className="qs-talk">
                <div className="qs-talk-agent">
                    {orb(inCall)}
                    <span className="qs-voice-name">{name}</span>
                    {companyName && <span className="qs-talk-company">{companyName}</span>}
                    {inCall && <span className="qs-live-pill">● Live</span>}
                    {inCall && maxDurationSeconds && (
                        <span className="qs-timer">
                            {formatSeconds(secondsElapsed)} <span>/ {formatSeconds(maxDurationSeconds)}</span>
                        </span>
                    )}
                </div>
                {live ? (
                    <div className="d-flex flex-column gap-2">
                        <p className="qs-eyebrow">Live transcript</p>
                        <div className="qs-transcript" aria-live="polite">
                            {messages.length === 0 && (
                                <p className="qs-hint">{connecting ? "Connecting…" : `${name} is about to greet you…`}</p>
                            )}
                            {messages.map((message, index) => (
                                <p key={index} className={`qs-bubble${message.sender === "user" ? " user" : ""}`}>{message.text}</p>
                            ))}
                            <div ref={transcriptEnd}></div>
                        </div>
                    </div>
                ) : (
                    <div className="qs-notice justify-content-center">
                        <h3 className="qs-title">Say hello to {name}</h3>
                        <p className="qs-text">Talk as one of your callers would. Your browser will ask to use your microphone.</p>
                        {micError && <p className="qs-error" role="alert">{micError}</p>}
                        <button type="button" className="qs-btn qs-btn-primary qs-btn-inline" onClick={() => startTest({})}>
                            <i className="ri-mic-line me-1"></i> Start talking
                        </button>
                    </div>
                )}
            </div>
            {live ? (
                <div className="qs-talk-footer">
                    <p className="qs-hint">Speak as a caller would.</p>
                    <button type="button" className="qs-btn qs-btn-danger" onClick={endTest}>End test</button>
                </div>
            ) : (
                <button type="button" className="qs-link" onClick={onFinished}>Skip the test for now</button>
            )}
        </>
    );
};

export default QuickStartTalk;
